import { useEffect, useState } from 'react'
import { Link } from 'wouter'
import { RoomWithName, JoinPolicy } from '@/types/rooms'
import { roomService } from '@/services/roomService'

const POLICY_LABELS: Record<JoinPolicy, string> = {
  open: 'Herkeste açık',
  approval: 'Onaylı',
  invite: 'Davetli'
}

const POLICY_STYLES: Record<JoinPolicy, string> = {
  open: 'bg-green-100 text-green-700',
  approval: 'bg-amber-100 text-amber-700',
  invite: 'bg-purple-100 text-purple-700'
}

export function JoinPolicyBadge({ policy }: { policy: JoinPolicy }) {
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${POLICY_STYLES[policy]}`}>
      {POLICY_LABELS[policy]}
    </span>
  )
}

const ROOMS_INTRO = (
  <div className="text-center mb-6">
    <h3 className="text-2xl font-bold text-gray-900">🔴 Canlı Çalışma Odaları</h3>
    <p className="text-gray-600 mt-2 max-w-xl mx-auto">
      Yalnız çalışmak zor geliyor? Bir odaya katıl, kronometreyi başlat — başka öğrencilerle
      aynı anda, sessizce çalış.
    </p>
    <Link href="/rooms" className="btn btn-primary inline-block mt-4">Odaya Katıl</Link>
  </div>
)

export function LiveRoomsStrip() {
  const [rooms, setRooms] = useState<RoomWithName[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    Promise.all([roomService.list(), roomService.listSite()])
      .then(([live, site]) => {
        if (cancelled) return
        setRooms(
          [...site, ...live].sort(
            (a, b) => (b.participant_count ?? 0) - (a.participant_count ?? 0)
          )
        )
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (loading) {
    return (
      <div className="mt-16">
        {ROOMS_INTRO}
        <p className="text-gray-400 text-sm text-center">Odalar yükleniyor…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="mt-16">
        {ROOMS_INTRO}
        <p className="text-gray-400 text-sm text-center">Odalar şu anda gösterilemiyor.</p>
      </div>
    )
  }

  if (rooms.length === 0) {
    return (
      <div className="mt-16">
        {ROOMS_INTRO}
        <p className="text-gray-500 text-sm text-center">
          Şu anda canlı oda yok — ilk odayı sen kur, arkadaşların katılsın.
        </p>
      </div>
    )
  }

  return (
    <div className="mt-16">
      {ROOMS_INTRO}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rooms.slice(0, 6).map((room) => (
          <Link key={room.code} href={`/rooms/${room.code}`}>
            <div className="card p-4 hover:shadow-lg transition-shadow cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-bold text-gray-900 truncate">{room.title}</h4>
                <JoinPolicyBadge policy={room.join_policy} />
              </div>
              {room.subject && <p className="text-sm text-gray-500 mt-1">{room.subject}</p>}
              <p className="text-sm font-semibold text-green-700 mt-2">
                👥 {room.participant_count} kişi çalışıyor
              </p>
            </div>
          </Link>
        ))}
      </div>
      <p className="text-center mt-4">
        <Link href="/rooms" className="text-blue-600 hover:underline font-semibold">
          Tüm odaları gör →
        </Link>
      </p>
    </div>
  )
}
